import {
    createContext,
    useContext,
    useEffect,
    useState,
} from "react";
import { useRouter } from "next/router";
import { withActiveToken } from "../lib/auth";
import { getAPIURL } from "../lib/utils";
import BusinessContext from "./business-context";
import { renderTables, OnCreateTable, renderPegs, onCreateRow, renderRows, updateTable } from "../lib/table";
import { extractEmailAddress, parseEmailFormat } from "../lib/mail-utils";
import { contactFormatter } from "../lib/contacts-util";

const ContactContext = createContext({
    fetchContactTable: () => { },
    contactTable: null,
    contacts: [],
    setContacts: () => { },
    createContactRow: () => { },
    fetchContacts: () => { },
    contactRows: null,
    updateContactList: () => { },
    setContactRows: () => { },
});

export const ContactProvider = (props) => {
    const { activeBusiness } = useContext(BusinessContext);
    const [contactTable, setContactTable] = useState(null);
    const [contacts, setContacts] = useState(null);
    const [contactRows, setContactRows] = useState(null);
    const router = useRouter();
    

    const createContactRow = (tableID, rowData) => {
        const callBack = (json) => {
            if(router.pathname === '/contacts') {
                fetchContacts(tableID);
            }
        };
        const onError = (error) => {
            console.error("Error creating contact row:", error);
        };
        onCreateRow(tableID, callBack, rowData, activeBusiness, onError, router);
    }

    const fetchContacts = (tableId) => {
        const callBack = (json) => {
            setContacts(contactFormatter(json));
            setContactRows(json)
        }

        const onError = (error) => {
            console.error("Error fetching contact rows:", error);
        };

        renderRows(tableId, callBack, activeBusiness, onError, router);
    }

    const initializeContactTable = () => {

        const callBack = (json) => {

            setContactTable(json);
            const columns = json.columns || [];
            const rowData = {
                inherit_business_permission: true,
                inherit_individual_permissions: true,
                namespace: "contact",
                position: 1000.0,
                data: {
                    [columns.find(col => col.name === "name")?.id]: "name",
                    [columns.find(col => col.name === "email")?.id]: "email",
                    [columns.find(col => col.name === "phone_no")?.id]: null,
                    [columns.find(col => col.name === "date_added")?.id]: "date"
                }
            };

            createContactRow(json.uuid, rowData);
        };

        const onError = (error) => {
            console.error(error);
        }

        const pegsCallback = (json) => {

            const schema = {
                "owner": 1,
                "business_permission": 4,
                "individual_permissions": [],
                "public_permission": 0,
                "name": "contacts",
                "namespace": "contact",
                "peg": json[0]?.id,
                "columns": [
                    {
                        "name": "name",
                        "type": "text",
                        "position": 1000.0,
                    },
                    {
                        "name": "email",
                        "type": "text",
                        "position": 2000.0,
                    },
                    {
                        "name": "phone_no",
                        "type": "number",
                        "position": 3000.0,
                    },
                    {
                        "name": "date_added",
                        "type": "text",
                        "position": 4000.0,
                    },
                ],
                "metadata": {}
            };

            OnCreateTable(schema, callBack, activeBusiness, onError, router);

        }
        renderPegs(pegsCallback, "emails", onError, router);
    }


    const fetchContactTable = () => {
        const callBack = (json) => {
            if (json.length > 0) {
                setContactTable(json[0]);
                fetchContacts(json[0]?.uuid);

            } else {
                initializeContactTable();

            }
        };
        const onError = (error) => {
            console.error(error);
        };

        renderTables(callBack, activeBusiness, onError, router, 'emails', 'contact');
    }

    const updateContactList = (contact) => {
        // Early return if no contact data or no contactTable
        if (!contact || !contactTable) {
            return;
        }

        const incomingRawEmails = [
            ...(Array.isArray(contact.to) ? contact.to : []),
            ...(Array.isArray(contact.cc) ? contact.cc : [])
        ].filter(email => email && email.trim() !== '');

        const existingEmails = (contacts && Array.isArray(contacts)) 
            ? contacts.map(c => c.email).filter(email => email && email.trim() !== '')
            : [];
        
        // Extract emails for comparison but keep the raw format
        const incomingExtractedEmails = incomingRawEmails.map(extractEmailAddress).filter(email => email && email.trim() !== '');
        
        // Remove duplicates from extracted emails and filter out existing ones
        const uniqueExtractedEmails = [...new Set(incomingExtractedEmails)];
        const newExtractedEmails = uniqueExtractedEmails.filter(email => !existingEmails.includes(email));

        if (newExtractedEmails.length === 0) {
            return;
        } else {
            const columns = contactTable?.columns || [];
            const lastPosition = contacts && contacts.length > 0
                ? Math.max(...contacts.map(c => c.position || 0))
                : 0;

            // Calculate starting position - ensure it's at least 1000 more than the last position
            const startingPosition = lastPosition === 0 ? 2000.0 : lastPosition + 1000.0;

            // Find the corresponding raw emails for the new extracted emails
            const newRawEmails = newExtractedEmails.map(extractedEmail => {
                return incomingRawEmails.find(rawEmail => 
                    extractEmailAddress(rawEmail) === extractedEmail
                );
            }).filter(Boolean);

            newRawEmails.forEach((rawEmail, idx) => {
                const rowData = {
                    inherit_business_permission: true,
                    inherit_individual_permissions: true,
                    namespace: "contact",
                    position: startingPosition + (idx * 1000.0),
                    data: {
                        [columns.find(col => col.name === "name")?.id]: parseEmailFormat(rawEmail) === extractEmailAddress(rawEmail) ? "" : parseEmailFormat(rawEmail),
                        [columns.find(col => col.name === "email")?.id]: extractEmailAddress(rawEmail) || "",
                        [columns.find(col => col.name === "date_added")?.id]: new Date().toISOString(),
                        [columns.find(col => col.name === "phone_no")?.id]: contact.phone_no || null
                    }
                };
                createContactRow(contactTable.uuid, rowData);
            });
        }
    }

    const addPhoneToContact = () => {
        if (!contactTable) return;

        const columns = contactTable.columns || [];
        const hasPhoneColumn = columns.some(col => col.name === "phone_no");

        if (!hasPhoneColumn) {
            const sortedColumns = columns.sort((a, b) => (a.position || 0) - (b.position || 0));
            const lastPosition = sortedColumns[sortedColumns.length - 1]?.position || 0;
            const secondLastPosition = sortedColumns[sortedColumns.length - 2]?.position || 0;
            const newPosition = (lastPosition + secondLastPosition) / 2;

            const newColumns = [
                ...columns,
                {
                    "name": "phone_no",
                    "type": "number",
                    "position": newPosition,
                }
            ];

            const payload = {
                ...contactTable,
                columns: newColumns
            };

            const callBack = (json) => {
                setContactTable(json);
            };

            const onError = (error) => {
                console.error("Error adding phone column:", error);
            };

            updateTable(contactTable.uuid, payload, callBack, activeBusiness, onError, router);
        }
    }


    useEffect(() => {
        if (contactTable) {
            addPhoneToContact();
        }
    }, [contactTable]);


    useEffect(() => {
        if (activeBusiness) {
            fetchContactTable();
        }
    }, [activeBusiness, router]);

    const context = {
        fetchContactTable,
        contactTable,
        contacts,
        setContacts,
        createContactRow,
        fetchContacts,
        updateContactList,
        contactRows,
        setContactRows,
    };

    return (
        <ContactContext.Provider value={context}>
            {props.children}
        </ContactContext.Provider>
    );
};

export default ContactContext;
